import {
  EVENT_LISTENER_METHODS,
  eventBus,
  startScreenSharing,
  switchCamera,
  toggleRaiseHand,
  toggleRecording,
  useBaseStore,
  useConfigStore,
  useHideRecordingButton,
  useHideScreenSharingButton,
  useHideVirtualBackgroundButton,
  useIsRecording,
  useIsSwitchCameraEnabledBrowser,
  useRaisedHandTimestamp,
  useTranslation,
} from '@cometchat/calls-sdk-core';
import React, { useCallback, useEffect, useRef } from 'react';
import { showConfirmationDialog } from '../confirmation-dialog/confirmation-dialog-state';
import type { IconName } from '../icon/icon-paths-24x24';
import MenuItem from './MenuItem.web';
import './MoreMenu.web.css';

interface MoreMenuProps {
  isVisible: boolean;
  onClose?: () => void;
  anchorElement?: HTMLElement | null;
  toggleSettings?: () => void;
  toggleVirtualBackground?: () => void;
  isMobileView?: boolean;
}

const MoreMenu: React.FC<MoreMenuProps> = ({
  isVisible,
  onClose,
  anchorElement,
  toggleSettings,
  toggleVirtualBackground,
  isMobileView = false,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const isMobileBrowser = useBaseStore(state => state.isMobileBrowser);
  const isSwitchCameraEnabled = useIsSwitchCameraEnabledBrowser();

  const positionMenu = useCallback(() => {
    if (!anchorElement || !menuRef.current) {
      return;
    }

    const anchorRect = anchorElement.getBoundingClientRect();
    const menuElement = menuRef.current;
    const menuRect = menuElement.getBoundingClientRect();

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Position above the anchor element by default using bottom positioning
    let bottom = viewportHeight - anchorRect.top + 8;
    let left = anchorRect.left + anchorRect.width / 2 - menuRect.width / 2;

    // Adjust horizontal position if menu would go outside viewport
    if (left < 8) {
      left = 8;
    } else if (left + menuRect.width > viewportWidth - 8) {
      left = viewportWidth - menuRect.width - 8;
    }

    // If menu would go above viewport, position below instead
    if (bottom + menuRect.height > viewportHeight - 8) {
      bottom = viewportHeight - anchorRect.bottom - 8;
    }

    // If menu would still go outside top viewport, adjust
    if (bottom + menuRect.height > viewportHeight - 8) {
      bottom = 8;
    }

    menuElement.style.bottom = `${bottom}px`;
    menuElement.style.left = `${left}px`;
    menuElement.style.top = 'auto'; // Reset top to auto when using bottom
  }, [anchorElement]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (isVisible && anchorElement && menuRef.current) {
        positionMenu();
      }
    };

    if (isVisible) {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isVisible, anchorElement, positionMenu]);

  // Position menu when it becomes visible
  useEffect(() => {
    if (isVisible && anchorElement && menuRef.current) {
      // Small delay to ensure DOM is updated
      requestAnimationFrame(() => {
        positionMenu();
      });
    }
  }, [isVisible, anchorElement, positionMenu, isMobileView]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !anchorElement?.contains(event.target as Node)
      ) {
        onClose?.();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isVisible, onClose, anchorElement]);

  const raiseHandTimestamp = useRaisedHandTimestamp();
  const isRecording = useIsRecording();
  const hideRecordingButton = useHideRecordingButton();
  const hideVirtualBackgroundButton = useHideVirtualBackgroundButton();
  const hideRaiseHandButton = useConfigStore(
    state => state.hideRaiseHandButton,
  );
  const hideScreenSharingButton = useHideScreenSharingButton();

  const handleItemClick = useCallback(
    (action: () => void) => {
      action();
      onClose?.();
    },
    [onClose],
  );

  const handleRecordingItemClick = useCallback(() => {
    eventBus.publish({
      type: EVENT_LISTENER_METHODS.ButtonClickListener
        .onRecordingToggleButtonClicked,
    });
    showConfirmationDialog(
      isRecording
        ? t('controlPanel.recording.stopRecording')
        : t('controlPanel.recording.startRecording'),
      isRecording
        ? t('controlPanel.recording.stopRecordingConfirmation')
        : t('controlPanel.recording.startRecordingConfirmation'),
      isRecording
        ? t('controlPanel.recording.stopRecording')
        : t('controlPanel.recording.startRecording'),
      t('controlPanel.recording.cancel'),
      () => {
        toggleRecording();
      },
    );
  }, [isRecording, t]);

  const handleVirtualBackground = () => {
    if (toggleVirtualBackground) {
      toggleVirtualBackground();
    }
  };
  const handleSettings = () => {
    if (toggleSettings) {
      toggleSettings();
    }
  };

  const commonItems = [
    !hideVirtualBackgroundButton && {
      iconName: 'virtual-background',
      label: 'Virtual Background',
      onClick: () => handleItemClick(handleVirtualBackground),
      id: 'vb',
    },
    !hideRecordingButton && {
      iconName: isRecording ? 'record-stop-fill' : 'record-fill',
      label: isRecording
        ? t('controlPanel.recording.stopRecording')
        : t('controlPanel.recording.startRecording'),
      onClick: () => handleItemClick(handleRecordingItemClick),
      id: 'rec',
    },
    // {
    //   iconName: 'stats' as IconName,
    //   label: 'Participants Stats',
    //   onClick: () => handleItemClick(handleParticipantsStats),
    //   id: 'pstats',
    // },
    !isMobileBrowser && {
      iconName: 'settings' as IconName,
      label: 'Settings',
      onClick: () => handleItemClick(handleSettings),
      id: 'settings',
    },
    isSwitchCameraEnabled && {
      iconName: 'switch-camera' as IconName,
      label: 'Switch Camera',
      onClick: () => {
        handleItemClick(switchCamera);
      },
      id: 'switch-camera',
    },
  ].filter(Boolean) as Array<{
    iconName: IconName;
    label: string;
    onClick: () => void;
    id: string;
    selected?: boolean;
  }>;
  const mobileOnlyItems = [
    !hideRaiseHandButton && {
      iconName: 'raise-hand-fill' as IconName,
      label: raiseHandTimestamp ? 'Lower Hand' : 'Raise Hand',
      onClick: () => handleItemClick(toggleRaiseHand),
      selected: Boolean(raiseHandTimestamp),
      id: 'raisehand',
    },
    !hideScreenSharingButton && {
      iconName: 'screen-share' as IconName,
      label: 'Start Screen Share',
      onClick: () => handleItemClick(startScreenSharing),
      id: 'screenshare',
    },
  ].filter(Boolean) as Array<{
    iconName: IconName;
    label: string;
    onClick: () => void;
    id: string;
    selected?: boolean;
  }>;
  const menuItems = isMobileView
    ? [...mobileOnlyItems, ...commonItems]
    : commonItems;

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <div
        className="cometchat-calls-more-menu-overlay"
        onClick={onClose}
        data-testid="more-menu-overlay"
      />
      <div
        ref={menuRef}
        className="cometchat-calls-more-menu-container"
        role="menu"
        aria-orientation="vertical">
        {menuItems.map(item => (
          <MenuItem
            key={item.id}
            iconName={item.iconName}
            label={item.label}
            onClick={item.onClick}
            selected={item.selected || false}
          />
        ))}
      </div>
    </>
  );
};

export default MoreMenu;
